import { useState, useEffect, useCallback, useRef } from 'react'
import { getApiBaseUrl } from '../../utils/apiConfig';
import { FileText, FileImage, FileCode, File, Code, Eye, Download, ExternalLink, BookOpen, Volume2, FileDown, Presentation, Copy } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import api from '../../services/api'
import { MarkdownPreview } from './MarkdownPreview'
import { AudioPlayer } from '../ui/audio-player'
import { SlideGallery } from '../SlideGallery'
import { slidesAPI } from '../../services/api'
import { CodeViewer } from '../ui/code-viewer'
import { JSONViewer } from '../ui/json-viewer'
import { logger } from '../../utils/logger'
import { DEFAULT_SLIDE_RESOLUTION } from '../../config/gsapViewer'

interface FileItem {
  name: string
  path: string
  type: 'file' | 'directory'
  size?: number
  extension?: string
}

interface FilePreviewProps {
  file: FileItem | null
  conversationId?: string
  onFileSelect?: (file: FileItem) => void
}

export function FilePreview({ file, conversationId, onFileSelect }: FilePreviewProps) {
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'code' | 'preview' | 'slides'>('code')
  const [slidesAvailable, setSlidesAvailable] = useState(false)

  // Create stable callback functions using useCallback
  const handleSlideClick = useCallback((slidePath: string) => {
    logger.debug('handleSlideClick called with:', slidePath);
    if (onFileSelect) {
      const fileItem = {
        name: slidePath.split('/').pop() || 'slide.html',
        path: slidePath,
        type: 'file' as const,
        extension: 'html'
      };
      logger.debug('Setting file and switching tab:', fileItem);
      onFileSelect(fileItem);
      setActiveTab('preview');
    }
  }, [onFileSelect, setActiveTab])

  useEffect(() => {
    if (!file || file.type === 'directory') {
      setContent('')
      return
    }

    loadFileContent(file)
  }, [file])

  // Check for slides availability when conversation changes
  useEffect(() => {
    const checkSlidesAvailability = async () => {
      if (!conversationId) {
        setSlidesAvailable(false)
        return
      }

      try {
        const data = await slidesAPI.getByConversation(conversationId)
        setSlidesAvailable((data.slides || []).length > 0)
      } catch (error) {
        setSlidesAvailable(false)
      }
    }

    checkSlidesAvailability()
  }, [conversationId])

  const loadFileContent = async (file: FileItem) => {
    try {
      setLoading(true)
      setError(null)
      
      // Use conversation-specific file reading
      const encodedPath = encodeURIComponent(file.path)
      const params = conversationId ? `?conversationId=${conversationId}` : ''
      const url = `/filesystem/read/${encodedPath}${params}`
      
      const response = await api.get(url)
      setContent(response.data.content)
    } catch (err) {
      logger.error('Error loading file content:', err)
      setError(err instanceof Error ? err.message : 'Failed to load file content')
    } finally {
      setLoading(false)
    }
  }


  const getFileIcon = () => {
    if (!file) return <File className="h-4 w-4" />
    
    const ext = file.extension?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')) {
      return <FileImage className="h-4 w-4 text-green-500" />
    }
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(ext || '')) {
      return <Volume2 className="h-4 w-4 text-orange-500" />
    }
    if (ext === 'pdf') {
      return <FileDown className="h-4 w-4 text-red-500" />
    }
    if (ext === 'md' || ext === 'markdown') {
      return <BookOpen className="h-4 w-4 text-primary" />
    }
    if (ext === 'json') {
      return <FileCode className="h-4 w-4 text-primary" />
    }
    if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'css', 'html'].includes(ext || '')) {
      return <FileCode className="h-4 w-4 text-primary" />
    }
    return <FileText className="h-4 w-4 text-gray-500" />
  }

  const isImageFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')
  }

  const isAudioFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(ext || '')
  }

  const isCodeFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'css', 'html'].includes(ext || '')
  }

  const isMarkdownFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ext === 'md' || ext === 'markdown'
  }

  const isHtmlFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ext === 'html' || ext === 'htm'
  }

  const isSlideFile = () => {
    if (!file) return false
    const filename = file.name.toLowerCase()
    return isHtmlFile() && filename.includes('slide')
  }

  const isPdfFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ext === 'pdf'
  }

  const isJsonFile = () => {
    const ext = file?.extension?.toLowerCase()
    return ext === 'json'
  }


  const enhanceHtmlForPreview = (htmlContent: string) => {
    // Add responsive viewport and base styles if not present
    const hasViewport = htmlContent.includes('viewport')
    const hasDoctype = htmlContent.trim().toLowerCase().startsWith('<!doctype')
    
    let enhancedContent = htmlContent
    
    // Add viewport meta tag if missing
    if (!hasViewport && htmlContent.includes('<head>')) {
      enhancedContent = enhancedContent.replace(
        '<head>',
        '<head>\n    <meta name="viewport" content="width=device-width, initial-scale=1.0">'
      )
    }
    
    // Add basic responsive styles if it's a complete HTML document
    if (hasDoctype || htmlContent.includes('<html>')) {
      if (!htmlContent.includes('<style>') && !htmlContent.includes('style.css')) {
        const responsiveStyles = `
          <style>
            body { margin: 0; padding: 16px; font-family: system-ui, -apple-system, sans-serif; }
            img { max-width: 100%; height: auto; }
            * { box-sizing: border-box; }
            @media (max-width: 768px) { body { padding: 8px; font-size: 14px; } }
          </style>
        `
        enhancedContent = enhancedContent.replace('</head>', responsiveStyles + '\n  </head>')
      }
    }
    
    return enhancedContent
  }

// Separate AudioPreview component for handling audio playback
function AudioPreview({ file, conversationId }: { file: FileItem; conversationId?: string }) {
  const getAudioUrl = () => {
    const cleanPath = file.path.startsWith('./') ? file.path.slice(2) : file.path
    const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/')
    const params = conversationId ? `?conversationId=${conversationId}` : ''
    // API_BASE_URL is now dynamically retrieved via getApiBaseUrl()
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/filesystem/audio/${encodedPath}${params}`
  }

  const downloadAudio = () => {
    const link = document.createElement('a')
    link.href = getAudioUrl()
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <AudioPlayer 
            src={getAudioUrl()} 
            title={file.name}
            className="mb-4"
          />
        </div>
      </div>

      <div className="border-t border-border bg-white p-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {file.size && (
              <div>Size: {formatFileSize(file.size)}</div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadAudio}>
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Separate SlidePreview component for handling slide HTML files with configurable resolution
function SlidePreview({ content, file, resolution = DEFAULT_SLIDE_RESOLUTION }: { content: string; file: FileItem; resolution?: { width: number; height: number } }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width - 32; // Account for padding
        const containerHeight = containerRect.height - 32;
        
        const scaleX = containerWidth / resolution.width;
        const scaleY = containerHeight / resolution.height;
        const optimalScale = Math.min(scaleX, scaleY);
        
        setScale(optimalScale);
      }
    };

    updateScale();
    
    const resizeObserver = new ResizeObserver(updateScale);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    window.addEventListener('resize', updateScale);
    
    return () => {
      window.removeEventListener('resize', updateScale);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className="h-full w-full bg-gray-50 p-4 flex items-center justify-center"
    >
      <div 
        className="relative bg-white shadow-lg rounded overflow-hidden"
        style={{
          width: `${resolution.width * scale}px`,
          height: `${resolution.height * scale}px`,
        }}
      >
        <iframe
          srcDoc={content}
          className="absolute inset-0 border-0 pointer-events-none"
          title={file.name}
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: `${resolution.width}px`,
            height: `${resolution.height}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }}
        />
      </div>
    </div>
  );
}

// Separate HtmlPreview component for handling HTML display with responsive scaling
function HtmlPreview({ content, file, enhanceHtmlForPreview, resolution = DEFAULT_SLIDE_RESOLUTION }: { content: string; file: FileItem; enhanceHtmlForPreview: (content: string) => string; resolution?: { width: number; height: number } }) {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    if (!containerRef) return

    const updateScale = () => {
      const containerRect = containerRef.getBoundingClientRect()
      const availableWidth = containerRect.width
      const availableHeight = containerRect.height
      
      const scaleX = availableWidth / resolution.width
      const scaleY = availableHeight / resolution.height
      const optimalScale = Math.min(scaleX, scaleY, 1) // Don't scale up, only down
      
      setScale(optimalScale)
    }

    updateScale()
    
    // Use ResizeObserver for better performance and accuracy
    const resizeObserver = new ResizeObserver(updateScale)
    resizeObserver.observe(containerRef)
    
    window.addEventListener('resize', updateScale)
    
    return () => {
      window.removeEventListener('resize', updateScale)
      resizeObserver.disconnect()
    }
  }, [containerRef])

  return (
    <div className="h-full w-full bg-gray-50 p-4">
      <div 
        ref={setContainerRef}
        className="h-full w-full flex items-center justify-center"
      >
        <div 
          className="relative bg-white shadow-lg rounded overflow-hidden"
          style={{
            width: `${resolution.width * scale}px`,
            height: `${resolution.height * scale}px`,
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        >
          <iframe
            srcDoc={enhanceHtmlForPreview(content)}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms"
            style={{
              background: 'white',
              width: `${resolution.width}px`,
              height: `${resolution.height}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left'
            }}
            title={`Preview of ${file.name}`}
          />
        </div>
      </div>
    </div>
  )
}

// Separate PDFPreview component for handling PDF display
function PDFPreview({ file, conversationId }: { file: FileItem; conversationId?: string }) {
  const getPdfUrl = () => {
    const cleanPath = file.path.startsWith('./') ? file.path.slice(2) : file.path
    const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/')
    const params = conversationId ? `?conversationId=${conversationId}` : ''
    // API_BASE_URL is now dynamically retrieved via getApiBaseUrl()
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/filesystem/read/${encodedPath}${params}`
  }

  const downloadPdf = () => {
    const link = document.createElement('a')
    link.href = getPdfUrl()
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const openInNewTab = () => {
    window.open(getPdfUrl(), '_blank')
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* PDF Viewer */}
      <div className="flex-1 p-4">
        <div className="h-full w-full bg-white rounded-lg border border-border">
          <iframe
            src={getPdfUrl()}
            className="w-full h-full rounded-lg"
            title={`PDF preview: ${file.name}`}
            style={{ minHeight: '500px' }}
          />
        </div>
      </div>

      {/* PDF info and actions bar */}
      <div className="border-t border-border bg-white p-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {file.size && (
              <div>Size: {formatFileSize(file.size)}</div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadPdf}>
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
            <Button variant="outline" size="sm" onClick={openInNewTab}>
              <ExternalLink className="h-3 w-3 mr-1" />
              Open
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Separate ImagePreview component for handling image display
function ImagePreview({ file, conversationId }: { file: FileItem; conversationId?: string }) {
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)

  logger.debug('ImagePreview initialized:', {
    fileName: file.name,
    filePath: file.path,
    conversationId: conversationId || 'NOT PROVIDED'
  })

  const getImageUrl = () => {
    // Properly encode path segments but preserve forward slashes for routing
    const cleanPath = file.path.startsWith('./') ? file.path.slice(2) : file.path
    const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/')
    const params = conversationId ? `?conversationId=${conversationId}` : ''
    // API_BASE_URL is now dynamically retrieved via getApiBaseUrl()
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/filesystem/image/${encodedPath}${params}`
    logger.debug('Constructing image URL:', {
      originalPath: file.path,
      cleanPath,
      encodedPath,
      finalUrl: url
    })
    return url
  }

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    logger.debug('Image loaded successfully:', {
      src: img.src,
      file: file.name,
      dimensions: `${img.naturalWidth}x${img.naturalHeight}`
    })
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    })
    setImageLoading(false)
    setImageError(false)
  }

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    logger.error('Image failed to load:', {
      src: event.currentTarget.src,
      file: file.name,
      error: event
    })
    setImageError(true)
    setImageLoading(false)
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const downloadImage = () => {
    const link = document.createElement('a')
    link.href = getImageUrl()
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const openInNewTab = () => {
    window.open(getImageUrl(), '_blank')
  }

  if (imageError) {
    return (
      <div className="h-full flex items-center justify-center text-destructive p-4">
        <div className="text-center">
          <FileImage className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>Failed to load image</p>
          <p className="text-sm text-muted-foreground">The image file could not be displayed</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={downloadImage}>
            <Download className="h-3 w-3 mr-1" />
            Download
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Image container with 1920x1080 aspect ratio */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex items-center justify-center min-h-full">
          {imageLoading && (
            <div className="text-muted-foreground">
              <FileImage className="h-12 w-12 mx-auto mb-2 animate-pulse" />
              <p>Loading image...</p>
            </div>
          )}
          <div 
            className="relative bg-black shadow-lg rounded"
            style={{
              aspectRatio: '16/9', // 1920:1080 = 16:9
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
          >
            <img
              src={getImageUrl()}
              alt={file.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              className={`w-full h-full object-contain ${imageLoading ? 'hidden' : ''}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            />
          </div>
        </div>
      </div>

      {/* Image info and actions bar */}
      {!imageLoading && !imageError && (
        <div className="border-t border-border bg-white p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground space-y-1">
              {imageDimensions && (
                <div>
                  Dimensions: {imageDimensions.width} × {imageDimensions.height}px
                </div>
              )}
              {file.size && (
                <div>
                  Size: {formatFileSize(file.size)}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadImage}>
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
              <Button variant="outline" size="sm" onClick={openInNewTab}>
                <ExternalLink className="h-3 w-3 mr-1" />
                Open
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

  if (!file) {
    return (
      <div className="h-full flex flex-col min-w-0 overflow-hidden">
        {/* Minimal header for empty state */}
        {slidesAvailable && (
          <div className="flex-shrink-0 p-2 border-b border-border/30">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-medium text-muted-foreground">Preview</h3>
              <Button
                variant={activeTab === 'slides' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('slides')}
                className="h-6 px-2 text-xs"
              >
                <Presentation className="h-2.5 w-2.5 mr-1" />
                Scenes
              </Button>
            </div>
          </div>
        )}
        
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeTab === 'slides' && slidesAvailable ? (
            <SlideGallery 
              conversationId={conversationId} 
              onSlideClick={handleSlideClick}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-900/50">
              <div className="text-center max-w-sm px-6">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <File className="h-8 w-8 opacity-40" />
                </div>
                <h3 className="text-sm font-medium mb-2">Your content will appear here</h3>
                <p className="text-xs text-muted-foreground/70 leading-relaxed">
                  {conversationId 
                    ? "Select a file from the browser to preview it, or start building something amazing with AI."
                    : "Start a conversation to generate and preview your content."
                  }
                </p>
                {slidesAvailable && (
                  <p className="text-xs mt-3 text-muted-foreground/70">Or view scenes from your conversation</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (file.type === 'directory') {
    return (
      <div className="h-full flex flex-col min-w-0 overflow-hidden">
        <div className="border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between p-3 pb-0">
            <h3 className="text-sm font-medium text-muted-foreground">Directory</h3>
          </div>
          
          {/* Show slides tab if slides are available */}
          {slidesAvailable && (
            <div className="flex border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none border-r border-border h-10 text-muted-foreground"
                disabled
              >
                <File className="h-3 w-3 mr-1.5" />
                Directory
              </Button>
              <Button
                variant={activeTab === 'slides' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('slides')}
                className="rounded-none h-10"
              >
                <Presentation className="h-3 w-3 mr-1.5" />
                Scenes
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeTab === 'slides' && slidesAvailable ? (
            <SlideGallery 
              conversationId={conversationId} 
              onSlideClick={handleSlideClick}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <File className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Directory selected</p>
                <p className="text-xs mt-1 text-muted-foreground">Choose a file to view its contents</p>
                {slidesAvailable && (
                  <p className="text-xs mt-1 text-muted-foreground">Or view scenes from the Scenes tab</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col min-w-0 overflow-hidden">
      {/* Modern Unified Header */}
      <div className="flex-shrink-0 p-3 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between">
          {/* File Info Section */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {getFileIcon()}
              <h3 className="text-sm font-medium truncate text-gray-900">{file.name}</h3>
              <Badge variant="outline" className="text-xs">
                {file.extension?.toUpperCase()}
              </Badge>
            </div>
            
            {/* Tabs - only for file content views */}
            {(isHtmlFile() || isMarkdownFile()) && (
              <div className="flex border border-gray-200 rounded-md bg-gray-50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('code')}
                  className={`h-7 px-3 text-xs rounded-l-md border-r border-gray-200 ${
                    activeTab === 'code' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <Code className="h-3 w-3 mr-1.5" />
                  Code
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('preview')}
                  className={`h-7 px-3 text-xs rounded-r-md ${
                    activeTab === 'preview' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  {isMarkdownFile() ? <BookOpen className="h-3 w-3 mr-1.5" /> : <Eye className="h-3 w-3 mr-1.5" />}
                  {isMarkdownFile() ? 'Rendered' : 'Preview'}
                </Button>
              </div>
            )}
          </div>
          
          {/* Action Buttons - only show when in code view */}
          {activeTab === 'code' && (isCodeFile() || isMarkdownFile()) && (
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(content)
                  } catch (err) {
                    logger.error('Failed to copy code:', err)
                  }
                }}
                className="h-7 px-2"
                title="Copy code"
              >
                <Copy className="h-3 w-3" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  const link = document.createElement('a')
                  const blob = new Blob([content], { type: 'text/plain' })
                  link.href = URL.createObjectURL(blob)
                  link.download = file.name
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  URL.revokeObjectURL(link.href)
                }}
                className="h-7 px-2"
                title="Download file"
              >
                <Download className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === 'slides' && slidesAvailable ? (
          <SlideGallery 
            conversationId={conversationId} 
            onSlideClick={handleSlideClick}
          />
        ) : loading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <p>Loading...</p>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-destructive">
            <p>Error: {error}</p>
          </div>
        ) : isImageFile() ? (
          <ImagePreview file={file} conversationId={conversationId} />
        ) : isAudioFile() ? (
          <AudioPreview file={file} conversationId={conversationId} />
        ) : isPdfFile() ? (
          <PDFPreview file={file} conversationId={conversationId} />
        ) : isSlideFile() && activeTab === 'preview' ? (
          <SlidePreview content={content} file={file} />
        ) : isHtmlFile() && activeTab === 'preview' ? (
          <HtmlPreview content={content} file={file} enhanceHtmlForPreview={enhanceHtmlForPreview} />
        ) : isMarkdownFile() && activeTab === 'preview' ? (
          <div className="h-full overflow-auto bg-white">
            <div className="p-6">
              <MarkdownPreview content={content} />
            </div>
          </div>
        ) : isJsonFile() ? (
          <JSONViewer 
            content={content}
            filename={file.name}
            showHeader={false}
            defaultFormatted={true}
          />
        ) : isCodeFile() || isMarkdownFile() ? (
          <CodeViewer 
            content={content}
            filename={file.name}
            showHeader={false}
          />
        ) : (
          <div className="p-4 overflow-auto h-full">
            <pre className="text-sm whitespace-pre-wrap">{content}</pre>
          </div>
        )}
      </div>
    </div>
  )
}